// MisReport.jsx

import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import Select from "react-select";

import {
  Download,
  Filter,
  ChevronDown,
  IndianRupee,
  Users,
  Activity,
  FlaskConical,
  TrendingUp,
  CalendarDays,
} from "lucide-react";

import styles from "./misReport.module.scss";

import SalesChart from "./salesChart";
import AppointmentChart from "./appointmentChart";
import PharmaSalesChart from "./pharmaSalesChart";
import PharmaSalesRetailerChart from "./pharmasalesRetailerChart";
import PatientChart from "./patientChart";
import TestProfileAnalyticsChart from "./testProfileAnalyticsChart";

import {
  getMISReportSlice,
  downloadMISExcelSlice,
} from "../../../store/slices/misReportSlice";

import { getPathologyServiceByClinic } from "../../../store/slices/serviceSlice";

const REPORTS = [
  {
    id: "sales",
    label: "Sales",
  },

  {
    id: "appointment",
    label: "Appointments",
  },

  {
    id: "testProfileAnalytics",
    label: "Test / Profile Analytics",
  },

  {
    id: "pharmacy",
    label: "Pharmacy",
  },

  {
    id: "pharmacyRetailer",
    label: "Retail",
  },

  {
    id: "patient",
    label: "Patients",
  },
];

const MisReport = () => {
  const dispatch = useDispatch();

  const [showFilter, setShowFilter] = useState(false);

  const [filters, setFilters] = useState({
    reportType: "sales",
    fromDate: "",
    toDate: "",
    selectedItems: [],
  });

  const [appliedFilters, setAppliedFilters] = useState({
    reportType: "sales",
    fromDate: "",
    toDate: "",
    selectedItems: [],
  });

  const { token } = useSelector((state) => state.auth || {});

  const { clinicInfo } = useSelector((state) => state.clinic || {});

  const { pathologyServices } = useSelector((state) => state.service || {});

  const { misData, loading, downloadLoading } = useSelector(
    (state) => state.misReport || {},
  );

  const clinicId = clinicInfo?.[0]?.clinicId;

  const showServiceFilter = filters.reportType === "testProfileAnalytics";

  useEffect(() => {
    if (!clinicId || !token) return;

    dispatch(
      getPathologyServiceByClinic({
        clinicId,
        token,
      }),
    );
  }, [clinicId, token, dispatch]);

  useEffect(() => {
    if (!clinicId || !token) return;

    const selectedTests =
      appliedFilters.reportType === "testProfileAnalytics"
        ? appliedFilters.selectedItems
            .filter((item) => item.type === "test")
            .map((item) => item.value)
        : [];

    const selectedProfiles =
      appliedFilters.reportType === "testProfileAnalytics"
        ? appliedFilters.selectedItems
            .filter((item) => item.type === "profile")
            .map((item) => item.value)
        : [];

    dispatch(
      getMISReportSlice({
        clinicId,
        token,
        reportType: appliedFilters.reportType,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
        selectedTests,
        selectedProfiles,
      }),
    );
  }, [appliedFilters, clinicId, token, dispatch]);

  const handleChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const formatDate = (date) => date.toISOString().split("T")[0];

  const handlePreset = (type) => {
    const today = new Date();

    const from = new Date();

    if (type === "today") {
      from.setDate(today.getDate());
    }

    if (type === "7days") {
      from.setDate(today.getDate() - 6);
    }

    if (type === "month") {
      from.setDate(today.getDate() - 29);
    }

    const updatedFilters = {
      ...filters,
      fromDate: formatDate(from),
      toDate: formatDate(today),
    };

    setFilters(updatedFilters);

    setAppliedFilters(updatedFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    const cleared = {
      reportType: filters.reportType,
      fromDate: "",
      toDate: "",
      selectedItems: [],
    };

    setFilters(cleared);

    setAppliedFilters(cleared);
  };

  const handleDownload = async () => {
    try {
      const selectedTests = appliedFilters.selectedItems
        .filter((item) => item.type === "test")
        .map((item) => item.value);

      const selectedProfiles = appliedFilters.selectedItems
        .filter((item) => item.type === "profile")
        .map((item) => item.value);

      const blob = await dispatch(
        downloadMISExcelSlice({
          clinicId,
          token,
          reportType: appliedFilters.reportType,
          fromDate: appliedFilters.fromDate,
          toDate: appliedFilters.toDate,
          selectedTests,
          selectedProfiles,
        }),
      ).unwrap();

      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");

      link.href = url;

      link.download = "mis-report.xlsx";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  const renderChart = () => {
    const data = misData?.details || [];
    switch (appliedFilters.reportType) {
      case "sales":
        return <SalesChart data={data} />;

      case "appointment":
        return <AppointmentChart data={data} />;

      case "pharmacy":
        return <PharmaSalesChart data={data} />;

      case "pharmacyRetailer":
        return <PharmaSalesRetailerChart data={data} />;

      case "patient":
        return <PatientChart data={data} />;

      case "testProfileAnalytics":
        return <TestProfileAnalyticsChart data={data} />;

      default:
        return null;
    }
  };

  return (
    <div className={styles["mis-page"]}>
      <div className={styles["mis-header"]}>
        <div>
          <h2 className={styles["mis-page-title"]}>MIS Dashboard</h2>

          <p className={styles["mis-page-subtitle"]}>
            Business analytics & performance
          </p>
        </div>

        <button
          onClick={handleDownload}
          disabled={downloadLoading}
          className={`${styles["mis-btn"]} ${styles["mis-btn--primary"]}`}
        >
          <Download size={16} />
          Export
        </button>
      </div>

      <div className={styles["mis-sub-tabs"]}>
        {REPORTS.map((item) => (
          <div
            key={item.id}
            className={
              filters.reportType === item.id
                ? `${styles["mis-sub-tab"]} ${styles["mis-sub-tab--active"]}`
                : styles["mis-sub-tab"]
            }
            onClick={() => {
              const updated = {
                ...filters,
                reportType: item.id,
              };

              setFilters(updated);

              setAppliedFilters(updated);
            }}
          >
            {item.label}
          </div>
        ))}
      </div>

      <div className={styles["mis-toolbar"]}>
        <div className={styles["mis-toolbar-top"]}>
          <div className={styles["mis-days-presets"]}>
            <button
              className={`${styles["mis-btn"]} ${styles["mis-btn--ghost"]}`}
              onClick={() => handlePreset("today")}
            >
              Today
            </button>

            <button
              className={`${styles["mis-btn"]} ${styles["mis-btn--ghost"]}`}
              onClick={() => handlePreset("7days")}
            >
              Last 7 Days
            </button>

            <button
              className={`${styles["mis-btn"]} ${styles["mis-btn--ghost"]}`}
              onClick={() => handlePreset("month")}
            >
              Last 30 Days
            </button>
          </div>

          <button
            className={styles["mis-filter-toggle"]}
            onClick={() => setShowFilter(!showFilter)}
          >
            <Filter size={18} />
            Filters
            <ChevronDown size={18} />
          </button>
        </div>

        {showFilter && (
          <div className={styles["mis-filter-panel"]}>
            <div className={styles["mis-filter-grid"]}>
              <div className={styles["mis-filter-group"]}>
                <label>From Date</label>

                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleChange}
                  className={styles["mis-input"]}
                />
              </div>

              <div className={styles["mis-filter-group"]}>
                <label>To Date</label>

                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleChange}
                  className={styles["mis-input"]}
                />
              </div>
            </div>

            {showServiceFilter && (
              <div className={styles["mis-service-filter"]}>
                <div className={styles["mis-filter-title"]}>
                  Test / Profile Analytics
                </div>

                <div className={styles["mis-filter-subtitle"]}>
                  Select tests or profiles to view analytics
                </div>

                <Select
                  isMulti
                  options={pathologyServices?.map((service) => ({
                    value: service._id,

                    label: service.testName || service.profileName,

                    type: service.testName ? "test" : "profile",
                  }))}
                  value={filters.selectedItems}
                  onChange={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      selectedItems: selected || [],
                    }))
                  }
                  placeholder="Search test or profile..."
                  classNamePrefix="mis-select"
                />
              </div>
            )}

            <div className={styles["mis-filter-actions"]}>
              <button
                className={`${styles["mis-btn"]} ${styles["mis-btn--primary"]}`}
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>

              <button
                className={`${styles["mis-btn"]} ${styles["mis-btn--ghost"]}`}
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles["mis-stats-row"]}>
        <div
          className={`${styles["mis-stat-card"]} ${styles["mis-stat-card--success"]}`}
        >
          <div className={styles["mis-stat-card__icon"]}>
            <IndianRupee size={22} />
          </div>

          <div className={styles["mis-stat-card__body"]}>
            <div className={styles["mis-stat-card__label"]}>Revenue</div>

            <div className={styles["mis-stat-card__value"]}>
              ₹{misData?.summary?.revenue || 0}
            </div>
          </div>
        </div>

        <div
          className={`${styles["mis-stat-card"]} ${styles["mis-stat-card--info"]}`}
        >
          <div className={styles["mis-stat-card__icon"]}>
            <Users size={22} />
          </div>

          <div className={styles["mis-stat-card__body"]}>
            <div className={styles["mis-stat-card__label"]}>Patients</div>

            <div className={styles["mis-stat-card__value"]}>
              {misData?.summary?.patients || 0}
            </div>
          </div>
        </div>

        <div
          className={`${styles["mis-stat-card"]} ${styles["mis-stat-card--warning"]}`}
        >
          <div className={styles["mis-stat-card__icon"]}>
            <CalendarDays size={22} />
          </div>

          <div className={styles["mis-stat-card__body"]}>
            <div className={styles["mis-stat-card__label"]}>Appointments</div>

            <div className={styles["mis-stat-card__value"]}>
              {misData?.summary?.appointments || 0}
            </div>
          </div>
        </div>

        <div
          className={`${styles["mis-stat-card"]} ${styles["mis-stat-card--danger"]}`}
        >
          <div className={styles["mis-stat-card__icon"]}>
            <FlaskConical size={22} />
          </div>

          <div className={styles["mis-stat-card__body"]}>
            <div className={styles["mis-stat-card__label"]}>Tests</div>

            <div className={styles["mis-stat-card__value"]}>
              {misData?.summary?.tests || 0}
            </div>
          </div>
        </div>

        <div
          className={`${styles["mis-stat-card"]} ${styles["mis-stat-card--purple"]}`}
        >
          <div className={styles["mis-stat-card__icon"]}>
            <TrendingUp size={22} />
          </div>

          <div className={styles["mis-stat-card__body"]}>
            <div className={styles["mis-stat-card__label"]}>Avg Revenue</div>

            <div className={styles["mis-stat-card__value"]}>
              ₹{misData?.summary?.avgRevenue || 0}
            </div>
          </div>
        </div>
      </div>

      <div className={styles["mis-chart-card"]}>
        <div className={styles["mis-chart-card__header"]}>
          <div>
            <h3 className={styles["mis-chart-card__title"]}>Analytics</h3>

            <p className={styles["mis-chart-card__subtitle"]}>
              View analytics based on selected filters
            </p>
          </div>
        </div>

        <div className={styles["mis-chart-area"]}>
          {loading ? (
            <div className={styles["mis-loading"]}>Loading...</div>
          ) : (
            renderChart()
          )}
        </div>
      </div>
    </div>
  );
};

export default MisReport;
